// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Ask Helios service.
 *
 * Calls the BFF's structured advisory endpoint (POST /api/v1/ai/ask-helios).
 * Unlike the streaming chat (chatService.streamChat), this is a single
 * request/response: the agent returns a complete envelope (answer text,
 * confidence, route, citations, abstained, disclaimer) which the UI renders
 * with confidence/citation chrome. Uses the shared api.ts wrapper (auth, 401
 * handling, JSON parsing).
 */

import { post } from './api';
import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { useShellStore } from '../stores/shellStore';
import type { AskHeliosRequestPayload, AskHeliosResponse, AskHeliosStreamEvent } from '../types';

export async function askHelios(payload: AskHeliosRequestPayload): Promise<AskHeliosResponse> {
  return post<AskHeliosResponse>(ENDPOINTS.AI_ASK_HELIOS, payload);
}

/**
 * Stream an Ask Helios answer over SSE (POST /ai/ask-helios/stream).
 *
 * Same envelope as `askHelios`, plus sanitized `progress` events emitted
 * while the orchestrator's tools run (e.g. "Searching the SOP library…") so
 * the UI isn't stuck on a static "thinking" message for the whole request.
 *
 * @param onProgress - Called with each sanitized progress label
 * @param onEnvelope - Called once with the final answer envelope
 * @param onError    - Called if the stream errors (network or model error)
 */
export async function streamAskHelios(
  payload: AskHeliosRequestPayload,
  onProgress: (_label: string) => void,
  onEnvelope: (_envelope: AskHeliosResponse) => void,
  onError: (_error: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const url = getApiUrl(ENDPOINTS.AI_ASK_HELIOS_STREAM);

  let token: string;
  try {
    token = await getTokenAsync();
  } catch {
    onError('Not authenticated. Please try again.');
    return;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError('Failed to connect to the AI service.');
    return;
  }

  if (response.status === 401) {
    removeUserData();
    useShellStore.getState().setSessionExpired(true);
    return;
  }

  if (!response.ok) {
    onError(`AI service error (${response.status})`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError('Streaming not supported by browser.');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') return;

        try {
          const event: AskHeliosStreamEvent = JSON.parse(data);
          if (event.error) {
            onError(event.error);
          } else if (event.progress) {
            onProgress(event.progress);
          } else if (event.text !== undefined) {
            onEnvelope(event as AskHeliosResponse);
          }
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError('Connection to AI service lost.');
  }
}
